import { FC, useCallback, useMemo } from 'react';
import { Divider, Grid, Stack, Typography } from '@mui/material';
import { FormikHelpers, FormikProvider, useFormik } from 'formik';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { z } from 'zod';
import { isBefore } from 'date-fns';
import isSameDay from 'date-fns/esm/fp/isSameDay/index';

import { ID, entityId } from 'domain/types/ID';
import { SubmitCustomerJobRequestData } from 'domain/repositories/IJobRepository';
import { ServiceType } from 'domain/entities/ServiceType';
import { Entity } from 'domain/types/Entity';
import useCaseSubmitCustomerJobRequest from 'application/jobs/useCases/useCaseSubmitCustomerJobRequest';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import { Button, Modal } from 'targets/web/components';
import { AirportAutocomplete } from 'targets/web/modules/jobs/components/JobDetailsForm/AirportAutocomplete';
import { FacilityAutocomplete } from 'targets/web/modules/jobs/components/JobDetailsForm/FacilityAutocomplete';
import {
  FormAircraftTypeAutocomplete,
  FormCustomerAutocomplete,
  FormikTextField,
} from 'targets/web/modules/dashboard/components';
import ButtonGroup from 'targets/web/components/ButtonGroup';
import Link from 'targets/web/components/Link';
import useSnackbar from 'targets/web/modules/dashboard/hooks/useSnackbar';
import { FormikDatePicker } from 'targets/web/modules/dashboard/components/FormikDatePicker/FormikDatePicker';
import { ProfitCenterAutocomplete } from 'targets/web/modules/jobs/components/JobDetailsForm/ProfitCenterAutocomplete';

import ServiceTypeAutocomplete from './ServiceTypeAutocomplete';
import NotesField from './NotesField';
import SpecialServiceNotesField from './SpecialServiceNotesField';
import AttachmentsSection from './AttachmentsSection';

interface Props {
  open: boolean;
  onClose: () => void;
}

const emptyInitialValues = {
  station: { id: ID(''), code: '', name: '' },
  airport: { id: ID(''), code: '', name: '', state: '' },
  facility: { id: ID(''), code: '', name: '', state: '' },
  customer: { id: ID(''), code: '', name: '' },
  type: { id: ID(''), code: '', name: '' },
  aircraft: {
    id: ID(''),
    code: '',
    type: { name: '', code: '' },
    typeId: ID(''),
  },
  serviceTypes: [],
  workType: 'aircraft',
  notes: '',
  aircraftRegNumber: '',
  aircraftSerialNumber: '',
  specialServiceNote: '',
  arrivalDate: null,
  departureDate: null,
  attachments: [],
};

type FormValues = typeof emptyInitialValues;

const RequestServiceModal: FC<Props> = ({ open, onClose }) => {
  const t = useTranslationPrefix('customer_portal.request_service_modal');

  const { isPending, submitCustomerJobRequest } = useCaseSubmitCustomerJobRequest();

  const enqueueSnackbar = useSnackbar();

  const validationSchema = useMemo(() => {
    const commonFields = {
      station: z.object({
        id: entityId(),
        code: z.string(),
        name: z.string(),
      }),
      airport: z.object({
        id: entityId(),
        code: z.string(),
        name: z.string(),
      }),
      facility: z.object({
        id: entityId(),
        name: z.string(),
      }),
      customer: z.object({
        id: entityId(),
        code: z.string().optional(),
        name: z.string().optional(),
      }),
      notes: z.string().max(250).optional(),
      serviceTypes: z.array(
        z.object({
          id: entityId(),
          name: z.string(),
          code: z.string(),
        }),
      ),
      attachments: z.array(
        z.object({
          name: z.string(),
          uri: z.string(),
          contentType: z.string(),
        }),
      ),
      arrivalDate: z.date({ message: t('date_invalid') }),
      departureDate: z.date({ message: t('date_invalid') }),
    };

    return toFormikValidationSchema(
      z.discriminatedUnion('workType', [
        z.object({
          ...commonFields,
          workType: z.literal('aircraft'),
          // aircraft type
          type: z.object({
            id: entityId(),
            code: z.string(),
            name: z.string(),
          }),
          aircraftRegNumber: z.string().optional(),
          aircraftSerialNumber: z.string().optional(),
        }),
        z.object({
          ...commonFields,
          workType: z.literal('specialService'),
          specialServiceNote: z.string().min(1).max(500),
        }),
      ]),
    );
  }, [t]);

  const handleSubmit = useCallback(
    (values: FormValues, helpers: FormikHelpers<FormValues>) => {
      const data: SubmitCustomerJobRequestData = {
        stationId: values.station.id,
        airportId: values.airport.id,
        customerId: values.customer.id,
        facilityId: values.facility.id,
        serviceNeeds: values.notes,
        requestedServicesIds: values.serviceTypes?.map((s: Entity<ServiceType>) => s.id) || [],
        arrivalDate: values.arrivalDate || new Date(),
        departureDate: values.departureDate || new Date(),
        attachments: values.attachments || [],
      };

      if (values.workType === 'specialService') {
        data.specialServiceNote = values.specialServiceNote;
      } else {
        data.aircraftTypeId = values.type?.id;
        data.aircraftRegNumber = values.aircraftRegNumber;
        data.aircraftSerialNumber = values.aircraftSerialNumber;
      }

      submitCustomerJobRequest(data)
        .then(() => {
          enqueueSnackbar(t('submit_success'), {
            variant: 'success',
          });
          helpers.resetForm();
          onClose();
        })
        .catch((e) => {
          enqueueSnackbar(
            t('submit_error', {
              message: (e as { body?: { message?: string } }).body?.message || 'unknown',
            }),
            {
              variant: 'error',
            },
          );
        });
    },
    [submitCustomerJobRequest, enqueueSnackbar, t, onClose],
  );

  const formik = useFormik({
    initialValues: emptyInitialValues,
    onSubmit: handleSubmit,
    validateOnMount: true,
    validateOnChange: true,
    validationSchema,
    validate: (values) => {
      const errors: Record<string, string> = {};

      // Add date validation here
      if (values.arrivalDate && values.departureDate) {
        if (
          !isBefore(values.arrivalDate, values.departureDate) &&
          !isSameDay(values.arrivalDate, values.departureDate)
        ) {
          errors.departureDate = t('departure_must_be_after_arrival');
        }
      }

      return errors;
    },
  });

  const disabled = false;

  return (
    <Modal
      title={t('title')}
      description={t('description')}
      open={open}
      maxWidth="lg"
      actions={{
        primary: {
          text: t('btn_submit'),
          onClick: () => {
            formik.handleSubmit();
          },
          loading: isPending,
        },
        secondary: { text: t('btn_cancel'), onClick: onClose },
      }}
      onClose={onClose}
      content={
        <FormikProvider value={formik}>
          <Stack spacing={4} direction="column">
            <Stack spacing={4} flex={1} direction="row">
              <Stack flex="50%" direction="column" spacing={4}>
                <Typography variant="h5">{t('section_primary')}</Typography>
                <ProfitCenterAutocomplete
                  label={t('label_profit_center')}
                  disabled={disabled}
                  stationEditable
                  autoSelectIfSingle
                />
                <AirportAutocomplete disabled={disabled} stationEditable />
                <FacilityAutocomplete disabled={disabled} />
                <FormCustomerAutocomplete
                  autoSelectIfSingle
                  disabled={disabled}
                  customerEditable={true}
                  hideAddCustomerButton
                />
                <Divider />
                <Stack direction="row" alignItems="center" spacing={4}>
                  <Typography variant="h5">{t('section_aircraft_part')}</Typography>
                  <ButtonGroup>
                    <Button
                      onClick={() => formik.setFieldValue('workType', 'aircraft')}
                      variant={formik.values.workType === 'aircraft' ? 'contained' : 'outlined'}
                    >
                      {t('btn_select_aircraft')}
                    </Button>
                    <Button
                      onClick={() => formik.setFieldValue('workType', 'specialService')}
                      variant={
                        formik.values.workType === 'specialService' ? 'contained' : 'outlined'
                      }
                    >
                      {t('btn_select_special_service')}
                    </Button>
                  </ButtonGroup>
                </Stack>
                <div>
                  {formik.values.workType === 'aircraft' && (
                    <Grid container spacing={4}>
                      <Grid item xs={6}>
                        <FormikTextField label={t('reg_label')} name="aircraftRegNumber" />
                      </Grid>
                      <Grid item xs={6}>
                        <FormikTextField label={t('serial_label')} name="aircraftSerialNumber" />
                      </Grid>
                      <Grid item xs={12}>
                        <FormAircraftTypeAutocomplete required />
                      </Grid>
                    </Grid>
                  )}
                  {formik.values.workType === 'specialService' && (
                    <Grid container spacing={4}>
                      <Grid item xs={12}>
                        <SpecialServiceNotesField />
                      </Grid>
                    </Grid>
                  )}
                </div>
              </Stack>
              <Stack flex="50%" direction="column" spacing={4}>
                <Typography variant="h5">{t('section_services')}</Typography>
                <ServiceTypeAutocomplete />
                <Divider />
                <NotesField />
                <Divider />
                <Typography variant="h5">{t('section_arrival_and_departure')}</Typography>
                <FormikDatePicker label={t('arrival_date_label')} required name="arrivalDate" />
                <FormikDatePicker label={t('departure_date_label')} required name="departureDate" />
                <Divider />
                <AttachmentsSection />
              </Stack>
            </Stack>
            <Stack pt={8} direction="row">
              <Typography sx={{ color: 'error.main' }}>*</Typography>
              <Typography color="text.secondary">&nbsp;-&nbsp;{t('required_fields')}</Typography>
            </Stack>
            <Divider />
            <Typography variant="inputHelper">
              {t('disclaimer')}&nbsp;
              <Link variant="inputHelper" color="text.secondary" href="#">
                {t('privacy_policy_link')}
              </Link>
            </Typography>
          </Stack>
        </FormikProvider>
      }
    ></Modal>
  );
};

export default RequestServiceModal;
